import React, { useCallback, useEffect, useState } from 'react';
import b2bService from '../../services/b2bService';

const STATUS_FILTERS = [['', 'All'], ['QUEUED', 'Waiting'], ['FAILED', 'Failed'], ['RETRY', 'Retry'], ['COMMITTED', 'Billed'], ['REJECTED', 'Rejected']];
const BADGE = { QUEUED: 'pending', RETRY: 'pending', PROCESSING: 'pending', COMMITTED: 'completed', FAILED: 'failed', REJECTED: 'failed' };

/** Orders that client systems sent in through the API: staff turn one into a bill, or refuse it with a reason. */
const B2bInboundPanel = () => {
  const [status, setStatus] = useState('QUEUED');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyRef, setBusyRef] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await b2bService.getInboundQueue(status));
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Could not load the orders.' });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const process = async (row) => {
    setBusyRef(row.trackingReferenceNo);
    setMessage(null);
    try {
      const res = await b2bService.processInboundOrder(row.trackingReferenceNo);
      if (res.status === 'COMMITTED') setMessage({ type: 'success', text: `Bill ${res.ledgerTransactionNo} raised for order ${row.clientOrderNumber}.` });
      else setMessage({ type: 'error', text: `${row.clientOrderNumber}: ${res.status === 'RETRY' ? 'a temporary error stopped it, try again.' : res.message || 'It could not be billed.'}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Could not process the order.' });
    } finally {
      setBusyRef('');
      load();
    }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Why is order ${row.clientOrderNumber} rejected?`);
    if (!reason || !reason.trim()) return;
    setBusyRef(row.trackingReferenceNo);
    setMessage(null);
    try {
      await b2bService.rejectInboundOrder(row.trackingReferenceNo, reason.trim());
      setMessage({ type: 'success', text: `Order ${row.clientOrderNumber} rejected.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Could not reject the order.' });
    } finally {
      setBusyRef('');
      load();
    }
  };

  const open = (s) => s === 'QUEUED' || s === 'RETRY' || s === 'FAILED';

  return (
    <div className="b2b-table-card">
      <div className="b2b-filter-bar" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="b2b-filter-group" role="group" aria-label="Order status">
          {STATUS_FILTERS.map(([code, label]) => (
            <button key={label} type="button" className={`b2b-tab-btn ${status === code ? 'active' : ''}`} onClick={() => setStatus(code)}>{label}</button>
          ))}
        </div>
        <button type="button" className="b2b-refresh-btn" style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      {message && (
        <div role="status" style={{ padding: '10px 16px', background: message.type === 'success' ? '#ecfdf5' : '#fef2f2', color: message.type === 'success' ? '#047857' : '#b91c1c', fontWeight: 600 }}>
          {message.text}
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>{loading ? 'Loading orders…' : 'No orders here.'}</div>
      ) : (
        <table className="b2b-data-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Client</th>
              <th>Client order no</th>
              <th>Patient</th>
              <th>Tests</th>
              <th>Status</th>
              <th>Bill / note</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.trackingReferenceNo}>
                <td>{new Date(r.receivedAt).toLocaleString('en-IN')}</td>
                <td>{r.partyName}</td>
                <td><strong>{r.clientOrderNumber}</strong><div style={{ fontSize: 11, color: '#64748b' }}>{r.trackingReferenceNo}</div></td>
                <td>{r.patientName || '—'}</td>
                <td>{r.testCount}</td>
                <td><span className={`b2b-badge ${BADGE[r.status] || 'pending'}`}>{r.status}</span></td>
                <td>{r.ledgerTransactionNo ? <strong>{r.ledgerTransactionNo}</strong> : (r.errorMessage || '')}</td>
                <td>
                  {open(r.status) && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="b2b-refresh-btn" style={{ background: '#2563eb', color: '#fff' }} disabled={busyRef === r.trackingReferenceNo} onClick={() => process(r)}>
                        {busyRef === r.trackingReferenceNo ? 'Working…' : 'Create bill'}
                      </button>
                      <button type="button" className="b2b-refresh-btn" disabled={busyRef === r.trackingReferenceNo} onClick={() => reject(r)}>Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default B2bInboundPanel;
